import { setup, assign, fromPromise, type ActorRefFrom } from 'xstate';

import { convertProfilesToProviders } from 'Src/behaviors/locations/utils';
import {
  EVENTS,
  LocationContext,
  LocationEvent,
  LocationResult,
  ProvidersResult,
  STATES
} from './types';
import { assertEventType } from '../utils';

export const createLocationV9Machine = (initialContext: LocationContext) =>
  setup({
    types: {
      context: {} as LocationContext,
      events: {} as LocationEvent
    },
    actions: {
      onFetchLocationSuccess: assign({
        location: ({ event }) => {
          assertEventType(event, EVENTS.fetchLocationSuccess);
          return event.output;
        }
      }),
      onFetchLocationError: assign({
        error: ({ event }) => {
          assertEventType(event, EVENTS.fetchLocationError);
          return event;
        }
      }),
      onFetchProvidersSuccess: assign({
        providers: ({ context, event }) => {
          assertEventType(event, EVENTS.fetchProvidersSuccess);
          return convertProfilesToProviders(
            event.output._result,
            context.locationId
          );
        },
        totalProviders: ({ event }) => {
          assertEventType(event, EVENTS.fetchProvidersSuccess);
          return event.output._metadata.total_providers;
        }
      }),
      onFetchProvidersError: assign({
        providersError: ({ event }) => {
          assertEventType(event, EVENTS.fetchProvidersError);
          return event;
        }
      }),
      incrementPage: assign({
        providersPage: ({ context }) => {
          return context.providersPage + 1;
        }
      }),
      setCurrentQuery: assign({
        currentQuery: ({ event }) => {
          assertEventType(event, EVENTS.newProvidersSearch);
          return event.data.currentQuery;
        }
      })
    },
    guards: {
      hasLocationIdInContext: ({ context }) => Boolean(context.locationId),
      hasLocationInContext: ({ context }) => Boolean(context.location),
      hasProvidersInContext: ({ context }) => Boolean(context.providers)
    },
    actors: {
      // this is the same fetch function that redux uses, modified slightly
      // so it returns the data rather than just call dispatch
      fetchLocation: fromPromise<
        LocationResult['data'],
        Pick<LocationContext, 'locationId' | 'fetchLocation' | 'searchSDK'>
      >(({ input }) =>
        input.fetchLocation({
          locationId: input.locationId,
          searchSDK: input.searchSDK
        })
      ),
      fetchProviders: fromPromise<
        ProvidersResult['data'],
        Pick<
          LocationContext,
          | 'locationId'
          | 'fetchProvidersByLocation'
          | 'currentQuery'
          | 'providersPage'
        >
      >(({ input }) =>
        input.fetchProvidersByLocation(input.locationId, {
          ...input.currentQuery,
          page: input.providersPage
        })
      )
    }
  }).createMachine({
    id: 'location-v9',
    context: {
      currentQuery: initialContext.currentQuery,
      fetchLocation: initialContext.fetchLocation,
      fetchProvidersByLocation: initialContext.fetchProvidersByLocation,
      location: initialContext.location,
      locationId: initialContext.locationId,
      providersPage: initialContext.providersPage,
      providers: initialContext.providers,
      totalProviders: initialContext.totalProviders,
      searchSDK: initialContext.searchSDK
    },
    type: 'parallel',
    states: {
      [STATES.location]: {
        initial: STATES.initializing,
        states: {
          [STATES.initializing]: {
            always: [
              { target: STATES.success, guard: 'hasLocationInContext' },
              { target: STATES.pending, guard: 'hasLocationIdInContext' },
              { target: STATES.error }
            ]
          },
          [STATES.pending]: {
            invoke: {
              id: 'fetchLocation',
              src: 'fetchLocation',
              input: ({ context }) => ({
                locationId: context.locationId,
                fetchLocation: context.fetchLocation,
                searchSDK: context.searchSDK
              }),
              onDone: {
                target: STATES.success,
                actions: 'onFetchLocationSuccess'
              },
              onError: {
                target: STATES.error,
                actions: 'onFetchLocationError'
              }
            }
          },
          [STATES.error]: {},
          [STATES.success]: {}
        }
      },
      [STATES.providers]: {
        initial: STATES.initializing,
        // These handlers lived on the root in v4. In v5 a root-level ".providers.pending"
        // target would also re-enter the location region (and refetch a failed
        // location); scoping them to this region matches v4.
        on: {
          [EVENTS.loadMoreProviders]: {
            target: `.${STATES.pending}`,
            actions: 'incrementPage'
          },
          [EVENTS.newProvidersSearch]: {
            target: `.${STATES.pending}`,
            actions: 'setCurrentQuery'
          }
        },
        states: {
          [STATES.initializing]: {
            always: [
              { target: STATES.success, guard: 'hasProvidersInContext' },
              { target: STATES.pending, guard: 'hasLocationIdInContext' },
              { target: STATES.error }
            ]
          },
          [STATES.pending]: {
            invoke: {
              id: 'fetchProviders',
              src: 'fetchProviders',
              input: ({ context }) => ({
                locationId: context.locationId,
                fetchProvidersByLocation: context.fetchProvidersByLocation,
                currentQuery: context.currentQuery,
                providersPage: context.providersPage
              }),
              onDone: {
                target: STATES.success,
                actions: 'onFetchProvidersSuccess'
              },
              onError: {
                target: STATES.error,
                actions: 'onFetchProvidersError'
              }
            }
          },
          [STATES.error]: {},
          [STATES.success]: {}
        }
      }
    }
  });

export type LocationStateMachine = ReturnType<typeof createLocationV9Machine>;
export type LocationActorRef = ActorRefFrom<LocationStateMachine>;
